/**
 * The sections, in the order the approved terminal layout fixes them.
 *
 * One list, used by the top bar, the mobile bar and the routes. Three copies
 * would drift, and the copy that drifted would be the one deciding what a
 * person can reach.
 *
 * Named for what the operator is looking for rather than for what the code
 * calls it. "Слоты" is an implementation word: a slot is the container, and
 * the thing being watched is the cycle running inside it. "Сканер" described
 * the machine doing the work rather than the answer it produces, which is a
 * ranking.
 *
 * `available: false` is not a missing link. A section that exists in the
 * product but not in this build says so when opened, because a person who
 * cannot see that it is coming assumes it is not.
 */
import {
  CircleHelp,
  FlaskConical,
  Activity,
  House,
  LayoutDashboard,
  Settings,
  ListOrdered,
  Server,
  ShieldAlert,
  Wallet,
  UserRound,
  type LucideIcon,
} from "lucide-react";

export type Section = {
  readonly path: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly available: boolean;
  /** Shown in the mobile bar, which carries five of these. */
  readonly onMobile: boolean;
  /** What the narrow mobile bar says, when the full name does not fit. */
  readonly short?: string;
};

export const SECTIONS: readonly Section[] = [
  { path: "/app", label: "Обзор", icon: LayoutDashboard, available: true, onMobile: true },
  { path: "/app/slots", label: "Циклы", icon: Activity, available: true, onMobile: true },
  { path: "/app/scanner", label: "Рейтинг", icon: ListOrdered, available: true, onMobile: true },
  { path: "/app/capital", label: "Капитал", icon: Wallet, available: true, onMobile: true },
  { path: "/app/risk", label: "Риски", icon: ShieldAlert, available: true, onMobile: false },
  { path: "/app/account", label: "Мой счёт", icon: UserRound, available: true, onMobile: false },
  // Connections, security, billing and help are its tabs rather than seven
  // more entries: a bar with a dozen items is a bar nobody reads to the end.
  { path: "/app/system", label: "Система", icon: Server, available: true, onMobile: false },
  // Last, and on purpose. The four before it are the day's work and the two
  // after them are the account's own state; this one is where the rules
  // themselves are read and changed. Singular, because it is one surface over
  // three contracts — the Strategy Contract, the Capital Policy and the
  // Allocation Policy — and not a list of strategies to browse. Its own tabs
  // live inside it, so the bar gains one entry rather than four.
  //
  // Available and reachable, but not a fifth bottom-bar item: the mobile bar
  // carries four destinations and "Ещё", and a sixth would push the account
  // itself off the row. It is reached from /app/more instead.
  { path: "/app/strategies", label: "Стратегия", icon: FlaskConical, available: true, onMobile: false },
];

/**
 * A client's cabinet: four places, named for what the client came to do
 * (owner, 2026-10-15). No Score, versions or pools — the platform runs the
 * strategy; the client watches the money, tops up the fee and sets up access.
 */
export const CLIENT_SECTIONS: readonly Section[] = [
  { path: "/app", label: "Главная", icon: House, available: true, onMobile: true },
  { path: "/app/slots", label: "Сделки", icon: Activity, available: true, onMobile: true },
  { path: "/app/billing", label: "Пополнения и комиссия", short: "Комиссия", icon: Wallet, available: true, onMobile: true },
  { path: "/app/account", label: "Настройки", icon: Settings, available: true, onMobile: true },
  { path: "/app/help", label: "Помощь", icon: CircleHelp, available: true, onMobile: false },
];

export function sectionsFor(roles: readonly string[], platform = false, client = false): readonly Section[] {
  if (client) return CLIENT_SECTIONS;
  if (platform) return [
    // The platform's own Score window (Рейтинг) sits beside the strategies it
    // ranks for; it needs no account.
    ...SECTIONS.filter((section) => ["/app", "/app/system", "/app/strategies", "/app/scanner"].includes(section.path)),
    { path: "/app/clients", label: "Клиенты", icon: UserRound, available: true, onMobile: true },
  ];
  return SECTIONS.filter((section) => section.path !== "/app/system" || roles.includes("OPERATOR"));
}
